import { useState, useEffect, useCallback } from "react";
import { Loader2, MessageCircle, Copy, Check, AlertCircle, CheckCircle2, Send, RefreshCw, UserPlus } from "lucide-react";
import { sincronizarCadastro, sincronizarAceite, consultarVinculo } from "../lib/manutencaoSync";

// Motivos que os sistemas devolvem para o aceite não estar vigente (iguais nos três).
const MOTIVO_ACEITE = {
  nunca_aceito: "nunca aceitou os termos",
  texto_mudou: "o texto mudou e precisa de novo aceite",
  vencido: "o aceite venceu (vale 12 meses)",
};

/**
 * O vínculo com um dos sistemas — a regra de qual link mandar.
 *
 * Existiam dois links para o mesmo fornecedor: o da ficha do CRM e o de envio
 * de documento do sistema. Quem estava na rua escolhia no olho. Aqui quem
 * escolhe é o CNPJ: se o fornecedor já existe lá, esta seção mostra o link DELE
 * (gerado lá, não uma cópia) e o que ainda falta; se não existe, diz para usar
 * a ficha do CRM. Um caminho de cada vez, sempre o mesmo.
 */
// Um bloco por sistema. Os tres respondem no MESMO formato — o que muda e a
// URL e a chave, resolvidas na serverless — entao a tela e uma so.
const ROTULOS = {
  manutencao: "Sistema de manutenção",
  abastecimento: "Sistema de combustível",
  rastreamento: "Sistema de rastreamento",
};

// `onEstado` devolve ao pai o que ESTE sistema respondeu. Serve para a ficha
// parar de cobrar documento que o fornecedor já mandou por lá: quem sabe disso
// é o sistema, não a lista do CRM.
export default function VinculoManutencao({ credenciamento, sistema = "manutencao", onEstado }) {
  const c = credenciamento || {};
  const cnpj = (c.cnpj || "").replace(/\D/g, "");
  const [estado, setEstado] = useState({ loading: false, dados: null });
  const [copiado, setCopiado] = useState(false);
  const [cadastrando, setCadastrando] = useState(false);
  const [msgCadastro, setMsgCadastro] = useState("");
  // Último resultado gravado pela serverless. Serve de rede quando a consulta
  // ao vivo falha: o link já conhecido continua na mão de quem está atendendo.
  const sync = c.integracaoSync?.[sistema] && typeof c.integracaoSync[sistema] === "object" ? c.integracaoSync[sistema] : null;

  const consultar = useCallback(async () => {
    if (cnpj.length !== 14) return;
    setEstado({ loading: true, dados: null });
    const dados = await consultarVinculo(cnpj, sistema);
    setEstado({ loading: false, dados });
    if (dados?.found) onEstado?.(sistema, dados);
  }, [cnpj, sistema, onEstado]);

  useEffect(() => { consultar(); }, [consultar]);

  // Cria o fornecedor lá a partir desta ficha e traz o link de documentos dele.
  // É o que destrava a ficha pendente: antes, sem cadastro na manutenção não
  // havia link nenhum para mandar, e a equipe ficava esperando.
  const cadastrar = async (acao = "cadastro") => {
    setCadastrando(true);
    setMsgCadastro("");
    const r = acao === "aceite" ? await sincronizarAceite(c.id, sistema) : await sincronizarCadastro(c.id, undefined, sistema);
    setCadastrando(false);
    if (r?.ok) { await consultar(); return; }
    setMsgCadastro(r?.erro || "Não foi possível falar com este sistema agora.");
  };

  const d = estado.dados;

  const copiar = async (url) => {
    try { await navigator.clipboard.writeText(url); setCopiado(true); setTimeout(() => setCopiado(false), 2000); } catch { /* ignora */ }
  };
  const whatsapp = (url) => {
    const tel = String(c.celular || c.telefone || "").replace(/\D/g, "");
    const base = tel ? `https://wa.me/${tel.length <= 11 ? "55" + tel : tel}` : "https://wa.me/";
    const texto = `Olá! Para concluir seu credenciamento na InstaSolutions, envie os documentos por aqui (o link expira em 7 dias):\n${url}`;
    return `${base}?text=${encodeURIComponent(texto)}`;
  };

  return (
    <div style={box}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
        <div style={titulo}>{ROTULOS[sistema] || sistema}</div>
        {cnpj.length === 14 && !estado.loading && (
          <button onClick={consultar} style={btnMini} title="Consultar de novo"><RefreshCw size={11} /> Atualizar</button>
        )}
      </div>

      {cnpj.length !== 14 && (
        <Nota>Informe o CNPJ na ficha para saber se este fornecedor já existe aqui.</Nota>
      )}

      {cnpj.length === 14 && estado.loading && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#64748b" }}>
          <Loader2 size={13} className="spin" /> Consultando pelo CNPJ...
        </div>
      )}

      {d?.configured === false && (
        <Nota>Integração ainda não configurada (falta a chave de acesso a este sistema). A ficha do CRM funciona normalmente.</Nota>
      )}

      {d?.error && (
        <div style={{ display: "flex", gap: 6, fontSize: 12, color: "#b45309" }}>
          <AlertCircle size={13} style={{ flexShrink: 0, marginTop: 1 }} /> {d.error}
        </div>
      )}

      {/* Ainda não é fornecedor lá → dá para criar a partir desta ficha */}
      {d?.configured && d?.found === false && !d?.error && (
        <div style={{ fontSize: 12, color: "#475569", lineHeight: 1.5 }}>
          {d.invalid ? (
            <>O CNPJ desta ficha não é válido — corrija antes de credenciar.</>
          ) : (
            <>
              Este CNPJ <strong>ainda não existe</strong> neste sistema — por isso não há link de documentos para mandar.
              <div style={{ marginTop: 10 }}>
                <button onClick={() => cadastrar()} disabled={cadastrando} style={{ ...btnAcao, background: "#7c3aed", color: "#fff", border: "none", opacity: cadastrando ? 0.7 : 1 }}>
                  {cadastrando ? <><Loader2 size={13} className="spin" /> Cadastrando...</> : <><UserPlus size={13} /> Cadastrar aqui e gerar link</>}
                </button>
              </div>
              <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 6 }}>
                Cria o fornecedor com os dados desta ficha. O aceite do contrato só é registrado quando a ficha for concluída.
              </div>
              {msgCadastro && (
                <div style={{ display: "flex", gap: 6, fontSize: 11, color: "#b45309", marginTop: 8 }}>
                  <AlertCircle size={12} style={{ flexShrink: 0, marginTop: 1 }} /> {msgCadastro}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Manutenção fora do ar: o link da última sincronização ainda serve */}
      {d?.error && sync?.upload_url && (
        <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 8, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: 11, color: "#475569", marginBottom: 8 }}>Último link conhecido desta ficha:</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <a href={whatsapp(sync.upload_url)} target="_blank" rel="noreferrer" style={{ ...btnAcao, background: "#25D366", color: "#fff", textDecoration: "none" }}>
              <MessageCircle size={13} /> Enviar link de documentos
            </a>
            <button onClick={() => copiar(sync.upload_url)} style={btnAcao}>
              {copiado ? <><Check size={13} color="#059669" /> Copiado</> : <><Copy size={13} /> Copiar link</>}
            </button>
          </div>
        </div>
      )}

      {/* Aviso de sincronização que ficou pelo caminho (aceite ou cadastro) */}
      {sync?.ok === false && sync?.erro && (
        <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a" }}>
          <div style={{ fontSize: 11, color: "#92400e", lineHeight: 1.4 }}>
            <strong>
              {sync.cadastro_manual ? "Cadastro manual neste sistema" : sync.gate ? "Aceite em espera" : sync.acao === "aceite" ? "O aceite não chegou" : "O cadastro não chegou"}
              {sync.gate || sync.cadastro_manual ? " — " : ": "}
            </strong>
            {sync.erro}
          </div>
          {sync.gate && (
            <div style={{ fontSize: 10, color: "#b45309", marginTop: 6, lineHeight: 1.4 }}>
              Mande o link de documentos acima. Assim que o documento de quem assina chegar, use "Tentar de novo" para fechar o aceite — os documentos da empresa não precisam estar completos.
            </div>
          )}
          {(!sync.permanente || sync.gate) && (
            <button onClick={() => cadastrar(sync.acao)} disabled={cadastrando} style={{ ...btnMini, marginTop: 8, borderColor: "#fcd34d", color: "#92400e" }}>
              {cadastrando ? <Loader2 size={11} className="spin" /> : <RefreshCw size={11} />} Tentar de novo
            </button>
          )}
        </div>
      )}

      {/* Resultado da última sincronização bem-sucedida — dispensa de assinatura,
          documentos anexados e campos que o sistema recebeu mas não leu. Só
          aparece quando o sistema devolve essas infos (combustível já devolve). */}
      {sync?.ok === true && (sync.assinatura_dispensada != null || sync.documentos_anexados || sync.campos_nao_mapeados?.length > 0) && (
        <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 8, background: sync.assinatura_dispensada === false ? "#fffbeb" : "#f0fdf4", border: `1px solid ${sync.assinatura_dispensada === false ? "#fde68a" : "#bbf7d0"}` }}>
          {sync.assinatura_dispensada === true && (
            <div style={{ display: "flex", gap: 6, fontSize: 11, color: "#166534" }}>
              <CheckCircle2 size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              <span><strong>Assinatura dispensada</strong> — o aceite do CRM foi aceito aqui (o hash do termo confere).</span>
            </div>
          )}
          {sync.assinatura_dispensada === false && (
            <div style={{ display: "flex", gap: 6, fontSize: 11, color: "#92400e" }}>
              <AlertCircle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              <span><strong>Termo divergente</strong> — o posto ainda precisa assinar neste sistema{sync.motivo_nao_dispensa ? ` (${sync.motivo_nao_dispensa})` : ""}.</span>
            </div>
          )}
          {sync.documentos_anexados && (
            <div style={{ fontSize: 10, color: "#166534", marginTop: sync.assinatura_dispensada != null ? 4 : 0 }}>
              Documentos anexados: {sync.documentos_anexados.gravados ?? 0}/{sync.documentos_anexados.recebidos ?? 0}
              {sync.documentos_anexados.ignorados?.length ? ` — ${sync.documentos_anexados.ignorados.length} ignorado(s)` : ""}
            </div>
          )}
          {sync.campos_nao_mapeados?.length > 0 && (
            <div style={{ fontSize: 10, color: "#b45309", marginTop: 4 }}>
              Campos que este sistema ainda não lê: {sync.campos_nao_mapeados.join(", ")}
            </div>
          )}
        </div>
      )}

      {/* Já é fornecedor lá → este é o único link a mandar */}
      {d?.found && (
        <div>
          <div style={{ fontSize: 12, color: "#334155", marginBottom: 8 }}>
            Fornecedor <strong>#{d.provider_id}</strong> — {d.nome}
          </div>

          {d.pendente ? (
            <div style={{ padding: "8px 10px", borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a", marginBottom: 10 }}>
              {d.docs_faltantes?.length > 0 && (
                <div style={{ fontSize: 11, color: "#92400e" }}>
                  <strong>Faltam documentos:</strong> {d.docs_faltantes.map((x) => x.label).join(", ")} <span style={{ color: "#b45309" }}>— cobram, mas não travam o aceite.</span>
                </div>
              )}
              {!d.aceite_vigente && (
                <div style={{ fontSize: 11, color: "#92400e", marginTop: d.docs_faltantes?.length ? 4 : 0 }}>
                  <strong>Aceite dos termos:</strong> {MOTIVO_ACEITE[d.aceite_motivo] || "pendente"}.
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 10px", borderRadius: 8, background: "#f0fdf4", border: "1px solid #bbf7d0", marginBottom: 10 }}>
              <CheckCircle2 size={14} color="#059669" />
              <span style={{ fontSize: 11, color: "#166534" }}>Credenciamento completo aqui.</span>
            </div>
          )}

          {d.docs_faltantes?.length > 0 && d.upload_url && (
            <>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <a href={whatsapp(d.upload_url)} target="_blank" rel="noreferrer" style={{ ...btnAcao, background: "#25D366", color: "#fff", textDecoration: "none" }}>
                  <MessageCircle size={13} /> Enviar link de documentos
                </a>
                <button onClick={() => copiar(d.upload_url)} style={btnAcao}>
                  {copiado ? <><Check size={13} color="#059669" /> Copiado</> : <><Copy size={13} /> Copiar link</>}
                </button>
              </div>
              <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 6 }}>
                Link gerado pelo próprio sistema — o documento cai direto no cadastro dele, sem login. Expira em 7 dias.
              </div>
            </>
          )}

          {d.docs_faltantes?.length === 0 && !d.aceite_vigente && (
            <div style={{ display: "flex", gap: 6, fontSize: 11, color: "#92400e" }}>
              <Send size={12} style={{ flexShrink: 0, marginTop: 1 }} />
              Documentos completos. Falta só o fornecedor aceitar os termos, e isso ele faz logado, em “Completar cadastro”.
            </div>
          )}
        </div>
      )}
      {/* A animação vive aqui: esta seção é usada dentro do modal, que não herda
          o keyframes declarado nas páginas de formulário. */}
      <style>{`.spin { animation: spin 1s linear infinite } @keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

function Nota({ children }) {
  return <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>{children}</div>;
}

const box = { padding: "12px 14px", borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0", marginBottom: 12 };
const titulo = { fontSize: 11, fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", letterSpacing: "0.05em" };
const btnMini = { display: "flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 10, fontWeight: 600, cursor: "pointer" };
const btnAcao = { display: "flex", alignItems: "center", gap: 5, padding: "7px 12px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer" };
